import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";

export type BankConnectionStatus = "pending" | "active" | "expired" | "revoked";

export interface BankConnectionSummary {
  id: string;
  provider: string;
  status: BankConnectionStatus;
  consentExpiresAt: string | null;
  tokenExpiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BankAccountSummary {
  id: string;
  connectionId: string | null;
  associationId: number | null;
  providerAccountId: string | null;
  iban: string | null;
  currency: string;
  accountName: string | null;
  accountType: string | null;
  lastSyncedAt: string | null;
  createdAt: string;
}

interface BankConnectionState {
  connections: BankConnectionSummary[];
  accounts: BankAccountSummary[];
  status: "idle" | "loading" | "failed";
  syncingAccountId: string | null;
}

const initialState: BankConnectionState = {
  connections: [],
  accounts: [],
  status: "idle",
  syncingAccountId: null,
};

export const fetchBankConnections = createAsyncThunk(
  "bankConnection/fetchConnections",
  async (associationId: number) => {
    const response = await axiosInstance.get("/bank/connections", {
      params: { associationId },
    });
    return response.data as BankConnectionSummary[];
  }
);

export const fetchBankAccounts = createAsyncThunk(
  "bankConnection/fetchAccounts",
  async (associationId: number) => {
    const response = await axiosInstance.get("/bank/accounts", {
      params: { associationId },
    });
    return response.data as BankAccountSummary[];
  }
);

export const initiateConnect = createAsyncThunk(
  "bankConnection/initiateConnect",
  async ({
    associationId,
    provider,
  }: {
    associationId: number;
    provider: string;
  }) => {
    const response = await axiosInstance.post("/bank/connect", null, {
      params: { associationId, provider },
    });
    return response.data as { authUrl: string } | { connected: true };
  }
);

export const revokeConnection = createAsyncThunk(
  "bankConnection/revoke",
  async ({ id, associationId }: { id: string; associationId: number }) => {
    await axiosInstance.delete(`/bank/connections/${id}`, {
      params: { associationId },
    });
    return id;
  }
);

export const triggerSync = createAsyncThunk(
  "bankConnection/triggerSync",
  async ({ id, associationId }: { id: string; associationId: number }) => {
    const response = await axiosInstance.post(
      `/bank/accounts/${id}/sync`,
      null,
      {
        params: { associationId },
      }
    );
    return { id, jobId: response.data.jobId };
  }
);

const bankConnectionSlice = createSlice({
  name: "bankConnection",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBankConnections.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchBankConnections.fulfilled, (state, action) => {
        state.status = "idle";
        state.connections = action.payload;
      })
      .addCase(fetchBankConnections.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(fetchBankAccounts.fulfilled, (state, action) => {
        state.accounts = action.payload;
      })
      .addCase(revokeConnection.fulfilled, (state, action) => {
        const conn = state.connections.find((c) => c.id === action.payload);
        if (conn) conn.status = "revoked";
      })
      .addCase(triggerSync.pending, (state, action) => {
        state.syncingAccountId = action.meta.arg.id;
      })
      .addCase(triggerSync.fulfilled, (state) => {
        state.syncingAccountId = null;
      })
      .addCase(triggerSync.rejected, (state) => {
        state.syncingAccountId = null;
      });
  },
});

export default bankConnectionSlice.reducer;
